/**
 * Web sibling of BottomNavBar.
 *
 * At desktop width the web shell renders a sidebar with the same
 * destinations (web/src/shell/Sidebar.tsx), so the bottom bar steps aside.
 * At phone and tablet widths the bar renders exactly as on the phone, but
 * pinned to the browser window: the page scrolls on web, so the column can
 * be taller than the window and an absolutely positioned bar would sit at
 * the bottom of the page instead of the bottom of the screen. The bar is
 * mounted inside a viewport-fixed layer that matches the column's width.
 * Metro never picks `.web.*` files.
 */
import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { BottomNavBar as MobileBottomNavBar } from './BottomNavBar.js';

const DESKTOP_MIN_WIDTH = 1024;
const TABLET_MIN_WIDTH = 768;
// Matches TABLET_CANVAS in web/src/shell/WebShell.tsx.
const TABLET_CANVAS_WIDTH = 600;

function readWidth(): number {
  const g = globalThis as unknown as { innerWidth?: number };
  return g.innerWidth ?? 0;
}

export const BottomNavBar: React.FC = () => {
  const [width, setWidth] = useState(readWidth);

  useEffect(() => {
    const g = globalThis as unknown as {
      addEventListener?: (t: string, h: () => void) => void;
      removeEventListener?: (t: string, h: () => void) => void;
    };
    const onResize = () => setWidth(readWidth());
    g.addEventListener?.('resize', onResize);
    return () => g.removeEventListener?.('resize', onResize);
  }, []);

  if (width >= DESKTOP_MIN_WIDTH) return null;
  const isTablet = width >= TABLET_MIN_WIDTH;

  return (
    <View style={styles.layer} pointerEvents="box-none">
      <View style={[styles.column, isTablet && styles.columnTablet]} pointerEvents="box-none">
        <MobileBottomNavBar />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  layer: {
    // `fixed` is valid in react-native-web; the RN types only list the
    // native values, hence the cast.
    position: 'fixed' as unknown as 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: 'center',
    zIndex: 100,
  },
  // Zero-height anchor; the mobile bar positions itself absolutely at its
  // bottom edge, which is the window's bottom edge.
  column: {
    width: '100%',
    position: 'relative',
  },
  columnTablet: {
    maxWidth: TABLET_CANVAS_WIDTH,
  },
});

export default BottomNavBar;
